import React, { useMemo } from 'react';
import { getTreePalette } from '../utils/seasonalPalette';

const TreeShelf = React.forwardRef(({ books, onBookClick }, snapshotRef) => {
  // Seasonal palette derived from the real local date (solo-only, no server/social
  // dependency — see seasonalPalette.js). Recomputed on mount/day-change via useMemo
  // keyed on the current date string, so it stays correct across a day rollover
  // without re-rendering on every tick.
  const dateKey = new Date().toDateString();
  const palette = useMemo(() => getTreePalette(new Date()), [dateKey]);

  // Generate consistent leaf color based on book title, drawn from the current
  // season's leaf color family so foliage still varies per-book while following
  // the real-world month/season.
  const getColor = (str) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    return palette.leafColors[Math.abs(hash) % palette.leafColors.length];
  };

  const getBlossomColor = (str) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    return palette.blossomColors[Math.abs(hash) % palette.blossomColors.length];
  };

  // Pre-defined positions for leaves (x, y coordinates on a 200x200 grid)
  const leafPositions = [
    { x: 100, y: 40 }, { x: 80, y: 50 }, { x: 120, y: 50 },
    { x: 60, y: 70 }, { x: 90, y: 75 }, { x: 110, y: 75 }, { x: 140, y: 70 },
    { x: 40, y: 90 }, { x: 70, y: 100 }, { x: 130, y: 100 }, { x: 160, y: 90 },
    { x: 50, y: 120 }, { x: 85, y: 125 }, { x: 115, y: 125 }, { x: 150, y: 120 },
    { x: 70, y: 145 }, { x: 130, y: 145 }
  ];

  // Fixed decorative snow-cap positions along the branch/trunk lines (winter only).
  const snowPositions = [
    { x: 100, y: 82 }, { x: 72, y: 82 }, { x: 128, y: 82 }, { x: 100, y: 102 }
  ];

  return (
    <div ref={snapshotRef} style={{ ...containerStyle, backgroundColor: palette.background.bg, border: `1px solid ${palette.background.border}` }}>
      {/* Inline styles for wind sway and spring blooming */}
      <style>
        {`
          @keyframes leafBloom {
            0% { transform: scale(0); opacity: 0; }
            70% { transform: scale(1.2); opacity: 0.85; }
            100% { transform: scale(1); opacity: 1; }
          }
          @keyframes leafSway {
            0% { transform: rotate(-3deg); }
            100% { transform: rotate(3deg) skewX(1deg); }
          }
          @keyframes trunkSway {
            0% { transform: rotate(-0.5deg) skewX(-0.5deg); }
            100% { transform: rotate(0.5deg) skewX(0.5deg); }
          }
          .cozy-leaf-group {
            transform-origin: 0px 0px;
            opacity: 0;
            transition: filter 0.2s, transform 0.2s cubic-bezier(0.175, 0.885, 0.32, 1.275);
          }
          .cozy-leaf-group:hover {
            filter: brightness(1.15) drop-shadow(0 3px 6px rgba(0,0,0,0.25));
            transform: scale(1.3) rotate(-2deg);
          }
        `}
      </style>

      <svg viewBox="0 0 200 220" style={{ width: '100%', maxWidth: '300px' }}>
        {/* Trunk & Branches with subtle atmospheric sway */}
        <g style={{ animation: 'trunkSway 6s ease-in-out infinite alternate', transformOrigin: '100px 220px' }}>
          <path
            d="M100 220 L100 160 Q100 120 70 80 M100 160 Q100 120 130 80"
            stroke={palette.trunkColor}
            strokeWidth="12"
            fill="none"
            strokeLinecap="round"
          />
          <path
            d="M100 160 L100 100"
            stroke={palette.trunkColor}
            strokeWidth="10"
            fill="none"
          />
        </g>

        {/* Winter: snow caps on the bare branches instead of full foliage */}
        {palette.showSnow && snowPositions.map((pos, i) => (
          <ellipse key={`snow-${i}`} cx={pos.x} cy={pos.y} rx="10" ry="4" fill="#ffffff" opacity="0.85" />
        ))}

        {/* Leaves (Books) — tinted to the current season/month palette */}
        {books.map((book, index) => {
          const pos = leafPositions[index % leafPositions.length];
          const color = palette.showBlossoms ? getBlossomColor(book.title) : getColor(book.title);
          const rotation = (index * 13) % 360;
          const rx = palette.showBlossoms ? 9 : (palette.bareBranches ? 10 : 14);
          const ry = palette.showBlossoms ? 9 : (palette.bareBranches ? 5 : 7);

          return (
            <g
              key={book.id}
              transform={`translate(${pos.x}, ${pos.y}) rotate(${rotation})`}
              onClick={() => onBookClick && onBookClick(book)}
              style={{ cursor: 'pointer' }}
            >
              {/* Separate coordinate transforms from wind sways */}
              <g
                className="cozy-leaf-group"
                style={{
                  animation: `leafBloom 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) forwards, leafSway ${3 + (index % 3)}s ease-in-out infinite alternate`,
                  animationDelay: `${index * 0.08}s, ${index * 0.12}s`,
                  opacity: palette.leafOpacity
                }}
              >
                <ellipse
                  cx="0" cy="0" rx={rx} ry={ry}
                  fill={color}
                  stroke="rgba(0,0,0,0.18)"
                  strokeWidth="0.75"
                  style={{ filter: 'drop-shadow(0 1.5px 2px rgba(0,0,0,0.12))' }}
                />
                {/* Gold-pressed elegant leaf vein overlay (skip on blossoms) */}
                {!palette.showBlossoms && (
                  <path
                    d={`M-${rx} 0 L${rx} 0`}
                    stroke="rgba(255,255,255,0.22)"
                    strokeWidth="0.75"
                  />
                )}
                <title>{book.title} by {book.author}</title>
              </g>
            </g>
          );
        })}
      </svg>
    </div>
  );
});

const containerStyle = {
  display: 'flex',
  justifyContent: 'center',
  marginTop: '2rem',
  padding: '20px',
  borderRadius: '12px',
};

export default TreeShelf;
